import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, Text, Flex, Image } from '@chakra-ui/react';
import { CbButton } from 'ui/components';
import arcadeApiClient from 'apiClients/arcadeApiClient';
import { AuthContext } from 'providers';
import { useIsEmbedded } from 'providers/MPEmbeddedContextProvider';
const IOS_STORE_URL = 'https://apps.apple.com/us/app/upduo-2-0/id6449320039';
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.upduo.app';
const ANDROID_MARKET_URL = 'market://details?id=com.upduo.app';
type Platform = 'ios-native' | 'android-native' | 'mobile-browser' | 'desktop';
function detectPlatform(): Platform {
  const ua = navigator.userAgent;

  // navigator.platform is NOT affected by WKWebView's customUserAgent, so it
  // reports the real device even when the old Arcade iOS app spoofs an
  // Android-looking UA string.
  const realPlatform = navigator.platform || '';
  const isRealIOS = /iPhone|iPad|iPod/.test(realPlatform) ||
  // iPad with "Request Desktop Website" reports "MacIntel" but has touch
  realPlatform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = isRealIOS || /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = !isRealIOS && /Android/i.test(ua);

  // iOS WebView detection — ordered by signal strength:
  // 1. Real iOS device (from navigator.platform) inside a WebView: no Safari token.
  // 2. Old Arcade iOS app token in the spoofed UA ("Arcade" or "ArcadeApp").
  if (isRealIOS && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua)) return 'ios-native';
  if (/\bArcadeApp\b|\bArcade\b/.test(ua)) return 'ios-native';

  // Android WebView includes "wv" in the UA string
  const isAndroidWebView = isAndroid && /wv/i.test(ua);
  if (isAndroidWebView) return 'android-native';
  if (isIOS || isAndroid) return 'mobile-browser';
  return 'desktop';
}
export const MigrationModal = () => {
  const {
    logout
  } = useContext(AuthContext);
  const {
    isEmbedded
  } = useIsEmbedded();
  const [migrationUrl, setMigrationUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const platform = useMemo(() => typeof window !== 'undefined' ? detectPlatform() : 'desktop', []);
  const isNativeApp = platform === 'ios-native' || platform === 'android-native';
  const isMobile = isNativeApp || platform === 'mobile-browser';
  const fetchMigrationToken = useCallback(() => {
    setIsLoading(true);
    setError(null);
    arcadeApiClient.post('/multi_platform/migration_token').then(res => {
      setMigrationUrl(res.data.migrationUrl);
    }).catch(err => {
      console.error('[MigrationModal] Failed to fetch migration token:', err);
      setError('Unable to generate migration link. Please try again later.');
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);
  useEffect(() => {
    if (isEmbedded || process.env.IS_PLAYWRIGHT) return;
    fetchMigrationToken();
  }, [isEmbedded, fetchMigrationToken]);

  // Gate: only show when we're NOT embedded in Arcade AI (iframe or webview).
  // Also skip during Playwright tests — the non-dismissable modal blocks all test flows.
  // The Modal below uses isOpen={true} because reaching this point means we passed these checks.
  if (isEmbedded || process.env.IS_PLAYWRIGHT) return null;
  const handleSwitchToUpduo = () => {
    if (!migrationUrl) return;
    window.location.href = migrationUrl;
  };
  const copyMigrationUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        // Clipboard write failed in both methods
      }
    }
  };
  const handleGetNewApp = async () => {
    if (!migrationUrl) return;
    const realPlatform = navigator.platform || '';
    const isRealIOS = /iPhone|iPad|iPod/.test(realPlatform) || realPlatform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isIOSPlatform = platform === 'ios-native' || isRealIOS;

    // Copy migration URL to clipboard first so the new app can read it.
    await copyMigrationUrl(migrationUrl);

    // Open the migration URL in a new tab. On iOS this triggers universal link
    // handling (opens the app if installed); on Android it triggers app links.
    // Using window.open keeps the current page alive so our fallback timer works.
    const popup = window.open(migrationUrl, '_blank');
    if (!popup) {
      // Popup was blocked — fall back to the store immediately
      window.location.href = isIOSPlatform ? IOS_STORE_URL : ANDROID_MARKET_URL;
      return;
    }

    // If the page is still visible after a delay, the app likely isn't
    // installed — fall back to the store by navigating the current page.
    const fallbackId = window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return;
      if (isIOSPlatform) {
        window.location.href = IOS_STORE_URL;
      } else {
        window.location.href = ANDROID_MARKET_URL;
        // If the market:// scheme isn't handled, fall back to the web URL
        window.setTimeout(() => {
          if (document.visibilityState === 'visible') {
            window.location.href = ANDROID_STORE_URL;
          }
        }, 500);
      }
    }, 1500);
    const cancelFallback = () => window.clearTimeout(fallbackId);
    window.addEventListener('pagehide', cancelFallback, {
      once: true
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') cancelFallback();
    }, {
      once: true
    });
  };
  return <Modal isOpen onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false} blockScrollOnMount={true} data-sentry-element="Modal" data-sentry-component="MigrationModal" data-sentry-source-file="MigrationModal.tsx">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" zIndex="2100" data-sentry-element="ModalOverlay" data-sentry-source-file="MigrationModal.tsx" />
      <ModalContent containerProps={{
      zIndex: '2100'
    }} data-sentry-element="ModalContent" data-sentry-source-file="MigrationModal.tsx">
        <ModalHeader textAlign="center" data-sentry-element="ModalHeader" data-sentry-source-file="MigrationModal.tsx">
          {isMobile ? 'Access Arcade AI' : 'Arcade is now Arcade AI'}
        </ModalHeader>
        <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="MigrationModal.tsx">
          <Flex justifyContent="center" mb={4} data-sentry-element="Flex" data-sentry-source-file="MigrationModal.tsx">
            <Image src="/images/arcade-ai-logo.png" alt="Arcade AI" boxSize="64px" borderRadius="12px" fallback={<></>} data-sentry-element="Image" data-sentry-source-file="MigrationModal.tsx" />
          </Flex>

          {error ? <>
              <Text mb={4} textAlign="center" color="red.500">
                {error}
              </Text>
              <Flex flexDirection="column" gap={4} mb={6}>
                <CbButton width="100%" onClick={fetchMigrationToken} isLoading={isLoading}>
                  Try Again
                </CbButton>
                <CbButton width="100%" variant="secondary" color="white" onClick={logout}>
                  Log Out
                </CbButton>
              </Flex>
            </> : <>
              <Text mb={2} textAlign="center">
                {isMobile ? 'Arcade has moved to Arcade AI. Download the new app to continue.' : 'Arcade has moved to Arcade AI. Click below to switch to your new account — no sign-up needed.'}
              </Text>
              <Text mb={2} textAlign="center" fontSize="sm" color="gray.500">
                Your account and data have already been transferred.
              </Text>
              {isMobile && <Text mb={4} textAlign="center" fontSize="sm" color="gray.500">
                  When prompted, allow the new app to paste.
                </Text>}
            </>}

          {!error && <Flex flexDirection="column" justify="flex-end" gap={4} mb={6}>
              {isMobile ? <CbButton width="100%" onClick={handleGetNewApp} isLoading={isLoading} isDisabled={!migrationUrl}>
                  Access New App
                </CbButton> : <CbButton width="100%" onClick={handleSwitchToUpduo} isLoading={isLoading} isDisabled={!migrationUrl}>
                  Switch to Arcade AI
                </CbButton>}
              <CbButton width="100%" variant="secondary" color="white" onClick={logout}>
                Log Out
              </CbButton>
            </Flex>}
        </ModalBody>
      </ModalContent>
    </Modal>;
};